"use client";

import { useState, useEffect } from "react"
import { useSearchParams } from "next/navigation"

import { API } from "@/services/api/API"
import Header from "@/components/ui/Header/Header"
import Search from "@/components/ui/Search"
import NewsNewestCard from "@/components/news/NewsNewestCard"
import NewsListCard from "@/components/news/NewsListCard"
import { Pagination } from "@/components/common"
import BookletAboutUs from "@/components/booklets/BookletAboutUs"
import NewsFilterCard from "./NewsFilterCard"
import NewsDetailsCard from "@/components/news/NewsDetailsCard"
import { NewsCategory, NewsDetails, NewsList, NewsYearMonth } from "@/@types"

type Props = {
  NewsList?: NewsList
  NewsCategory: NewsCategory[]
  NewsYearMonth: NewsYearMonth[]
  NewestNews: NewsDetails
  NewsDetails?: NewsDetails
  showNewestCard?: boolean
  categorySlug?: string
}

export default function NewsContainer({
  NewsList: initialNewsList,
  NewsCategory,
  NewsYearMonth,
  NewestNews,
  NewsDetails,
  showNewestCard = true,
  categorySlug,
}: Props) {
  const searchParams = useSearchParams();
  const currentUrlQuery = searchParams.get('query') ?? '';
  const currentPage = searchParams.get('page') ?? '1';
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [displayedNewsList, setDisplayedNewsList] = useState<NewsList | undefined>(initialNewsList);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);

  useEffect(() => {
    const fetchSearchResults = async (query: string) => {
      if (query.length > 2 || currentPage !== '1') {
        setIsLoadingSearch(true);
        try {
          let results;
          if (query.length > 2) {
            results = await API.news.getNewsListBySearch(query);
          } else {
            results = await API.news.getNewsList({ page: currentPage });
          }
          setDisplayedNewsList(results);
        } catch (error) {
          console.error("Error fetching search results:", error);
          setDisplayedNewsList(undefined);
        } finally {
          setIsLoadingSearch(false);
        }
      } else {
        setDisplayedNewsList(initialNewsList);
        setIsLoadingSearch(false);
      }
    };

    fetchSearchResults(currentUrlQuery);

  }, [currentUrlQuery, initialNewsList, currentPage]);


  const toggleFilterModal = () => {
    setIsFilterModalOpen((prev) => !prev)
  }

  const currentPageForPagination = parseInt(searchParams.get('page') || '1', 10);

  return (
    <div className="bg-silver">
      <Header
        variant="dark"
        description="Descubre información sobre maquinaria pesada de marcas líderes. Aprende a mantener tu equipo en óptimas condiciones y maximizar su uso."
      />
      <div className="container pb-32">
        {showNewestCard && <NewsNewestCard NewestNews={NewestNews} />}
        <div className="grid lg:grid-cols-[25%_90%] mt-8">
          <div className="grid grid-cols-1 auto-rows-min gap-4">
            <Search
              onOpenFilters={toggleFilterModal}
              baseSearchPath="/noticias"
            />
            <NewsFilterCard
              isMenuOpen={isFilterModalOpen}
              toggleMenu={toggleFilterModal}
              categories={NewsCategory}
              yearMonths={NewsYearMonth}
              selectedCategory={categorySlug}
            />
            <div className="hidden lg:block">
              <BookletAboutUs />
            </div>
          </div>
          <div className="w-full lg:w-[84%] lg:pl-4 lg:pt-0">
            {NewsDetails ? (
              <NewsDetailsCard newsDetails={NewsDetails} />
            ) : (
              <>
                <h2 className="sr-only">Listado de noticias</h2>
                <NewsListCard newsList={displayedNewsList} categorySlug={categorySlug} />
                {displayedNewsList && displayedNewsList.pagination.lastPage > 1 && (
                  <Pagination
                    result="Noticias"
                    currentPage={currentPageForPagination}
                    lastPage={displayedNewsList?.pagination.lastPage}
                    totalResults={displayedNewsList?.pagination.total}
                    perPage={displayedNewsList?.pagination.perPage}
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div className="block lg:hidden mt-8">
          <BookletAboutUs />
        </div>
      </div>
    </div>
  );
}
